import {
  AUTO_OCR_ACTIVE_STATUSES,
  AUTO_OCR_MAX_POLL_COUNT,
  AUTO_OCR_POLL_INTERVAL_MS,
} from '@/pages/prescriptions/prescriptionOcrUtils';
import { prescriptionKeys } from '@/entities/prescription/hooks/usePrescriptions';
import { prescriptionService } from '@/entities/prescription/api/prescription.api';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

/** 자동 OCR 매칭 진행 중일 때 이미지 상세·의약품 목록 폴링 */
export const usePrescriptionAutoOcrPolling = (
  prescriptionImageCode: string | null,
  refYm: string | null,
  ocrStatus?: string,
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      !prescriptionImageCode ||
      !ocrStatus ||
      !AUTO_OCR_ACTIVE_STATUSES.has(ocrStatus as never)
    ) {
      return undefined;
    }

    let canceled = false;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    let pollCount = 0;

    const poll = async () => {
      if (canceled || pollCount >= AUTO_OCR_MAX_POLL_COUNT) return;
      pollCount += 1;

      try {
        const detail = await prescriptionService.getPrescriptionImageDetail(
          prescriptionImageCode,
        );
        if (canceled) return;

        queryClient.setQueryData(
          prescriptionKeys.imageDetail(prescriptionImageCode),
          detail,
        );

        if (detail.ocrStatus === 'SUCCEEDED') {
          const targetRefYm = refYm || detail.prescriptionYearMonth;
          if (targetRefYm) {
            await queryClient.invalidateQueries({
              queryKey: prescriptionKeys.drugs(prescriptionImageCode, targetRefYm),
            });
          }
          return;
        }

        if (!detail.ocrStatus || !AUTO_OCR_ACTIVE_STATUSES.has(detail.ocrStatus as never)) {
          return;
        }
      } catch {
        // 폴링 실패는 다음 주기에 재시도
      }

      timerId = setTimeout(() => void poll(), AUTO_OCR_POLL_INTERVAL_MS);
    };

    timerId = setTimeout(() => void poll(), AUTO_OCR_POLL_INTERVAL_MS);

    return () => {
      canceled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [prescriptionImageCode, refYm, ocrStatus, queryClient]);
};
